import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Progress from 'antd/es/progress';
import Space from 'antd/es/space';
import { useEffect, useRef } from 'react';
import { BulkReconciliationPreview } from 'types/incidences';
import { useReconciliationQueue } from '../hooks/useReconciliationQueue';
import styles from '../BulkReconciliationPage.module.scss';

interface Props {
  queue: ReturnType<typeof useReconciliationQueue>;
  preview: BulkReconciliationPreview;
  onBack: () => void;
}

export const ReconciliationProgress = ({ queue, preview, onBack }: Props) => {
  const { employees, status, notice, counts, pause, resume } = queue;
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [status]);
  const current = employees.find(
    (employee) => employee.status === 'processing'
  );
  const confirmed = counts.completed + counts.failed;
  const percent = employees.length
    ? Math.floor((confirmed * 100) / employees.length)
    : 0;
  const finished = status === 'finished';
  const title = finished
    ? counts.unconfirmed > 0
      ? 'Conciliación con resultados sin confirmar'
      : counts.failed > 0
        ? 'Conciliación completada parcialmente'
        : 'Conciliación completada'
    : status === 'paused'
      ? 'Conciliación pausada'
      : 'Conciliando incidencias';
  const failures = employees.filter((employee) => employee.status === 'failed');
  const unconfirmed = employees.filter(
    (employee) => employee.status === 'unconfirmed'
  );

  return (
    <Card className={styles.progress}>
      <h2 ref={headingRef} tabIndex={-1}>
        {title}
      </h2>
      <p>
        {preview.contractKind.name} · Desde {preview.applicationDate}
      </p>
      <Progress
        aria-label="Progreso confirmado de conciliación"
        aria-valuetext={`${confirmed} de ${employees.length} procesados; ${counts.completed} conciliados y ${counts.failed} fallidos`}
        percent={percent}
        status={
          finished && !counts.failed && !counts.unconfirmed
            ? 'success'
            : 'normal'
        }
      />
      <div role="status" aria-live="polite" aria-atomic="true">
        <p>
          {confirmed} de {employees.length} procesados · {counts.completed}{' '}
          conciliados · {counts.failed} fallidos
        </p>
        <p>
          {counts.pending} pendientes · {counts.unconfirmed} sin confirmar
        </p>
        {current && (
          <p>
            Conciliando:{' '}
            <strong>
              {current.employeeNumber} — {current.employeeName}
            </strong>
          </p>
        )}
        {status === 'pausing' && (
          <p>Pausando al terminar el empleado actual…</p>
        )}
      </div>
      {notice && (
        <Alert type="warning" showIcon title={notice} className="mb-3" />
      )}
      {!finished && (
        <p>
          Mantén esta pestaña abierta. Al recargar o salir perderás el
          seguimiento.
        </p>
      )}
      {failures.length > 0 && (
        <section aria-label="Empleados fallidos">
          <h3>Empleados que no se pudieron conciliar</h3>
          <ul>
            {failures.map((employee) => (
              <li key={employee.employeeId}>
                <strong>
                  {employee.employeeNumber} — {employee.employeeName}
                </strong>
                : {employee.message}
              </li>
            ))}
          </ul>
        </section>
      )}
      {unconfirmed.length > 0 && (
        <section aria-label="Resultados sin confirmar">
          <h3>Resultados sin confirmar</h3>
          <p>
            Estos empleados no se volverán a enviar. Verifica su resultado antes
            de iniciar otra conciliación para ellos.
          </p>
          <ul>
            {unconfirmed.map((employee) => (
              <li key={employee.employeeId}>
                {employee.employeeNumber} — {employee.employeeName}
              </li>
            ))}
          </ul>
        </section>
      )}
      <Space wrap>
        {(status === 'running' || status === 'pausing') && (
          <Button onClick={pause} disabled={status === 'pausing'}>
            {status === 'pausing' ? 'Pausando…' : 'Pausar'}
          </Button>
        )}
        {status === 'paused' && counts.pending > 0 && (
          <Button type="primary" onClick={() => void resume()}>
            Continuar pendientes
          </Button>
        )}
        <Button onClick={onBack}>Volver a empleados</Button>
      </Space>
    </Card>
  );
};
